import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const AuthWrapper = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [isChecking, setIsChecking] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();

  // Validate any stored token against the server; stale/forged tokens
  // (including the old client-side 'authenticated' constant) get cleared
  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setIsChecking(false);
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('auth_token');
        }
      } catch (e) {
        // Network error (offline / server cold start) — keep the token and
        // let the app proceed with cached data rather than locking out
        console.error('Token verification failed:', e);
        setIsAuthenticated(true);
      }
      setIsChecking(false);
    };

    verifyToken();
  }, []);

  // Skip auth for cleaner calendar routes
  if (location.pathname.startsWith('/calendar/')) {
    return children;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      if (response.ok) {
        const { token } = await response.json();
        localStorage.setItem('auth_token', token);
        // Reload so App's bootstrap fetch re-runs with the token present —
        // otherwise a first-ever login lands on an empty app until a manual
        // refresh
        window.location.reload();
        return;
      }

      if (response.status === 401) {
        alert('Incorrect password');
      } else if (response.status === 429) {
        alert('Too many login attempts. Please wait 15 minutes and try again.');
      } else {
        alert('Login failed. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Could not reach the server. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isChecking) {
    return null;
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-sm w-full space-y-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2.5 mb-6">
              <div className="w-2.5 h-2.5 rounded-full bg-violet-500 shadow-lg shadow-violet-500/50"></div>
              <span className="text-xl font-semibold text-zinc-100 tracking-tight">G Homes</span>
            </div>
            <h2 className="text-2xl font-semibold text-zinc-100">
              Sign in
            </h2>
            <p className="mt-1 text-sm text-zinc-500">Enter your password to continue</p>
          </div>
          <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <div>
              <label htmlFor="password" className="sr-only">Password</label>
              <input
                id="password"
                type="password"
                required
                className="block w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500 text-sm transition-colors"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center py-2.5 px-4 rounded-lg text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 focus:ring-offset-zinc-950 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Signing in…' : 'Sign in'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return children;
};

export default AuthWrapper;